import React, { useEffect, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Grid,
  Tag,
  Popconfirm,
  Avatar,
  Tooltip,
  Progress,
  Select
} from 'antd';
import { Link } from 'react-router-dom';
import {
  SearchOutlined,
  RightOutlined,
  SyncOutlined,
  CarOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ToolOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { synchronizationService, citiesService } from '../../common/services';
import Can from '../../components/can';

// Couleur avatar dynamique
const getAvatarColor = (text) => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'];
  if (!text) return colors[0];
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const SyncVehicules = () => {
  // Liste des véhicules non synchronisés
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Villes
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Statistiques
  const [stats, setStats] = useState({
    totalYandex: 0,
    totalTigoon: 0,
    notInTigoon: 0,
    syncRate: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // Recherche + pagination
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Sync en cours
  const [syncingCarId, setSyncingCarId] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  // Charger les villes au montage
  useEffect(() => {
    citiesService.getAll()
      .then((res) => {
        const citiesData = res?.data || [];
        const activeCities = citiesData.filter(c => c.isActive);
        setCities(activeCities);
        const defaultCity = activeCities.find(c => c.isDefault);
        if (defaultCity) {
          setSelectedCity(defaultCity._id);
        } else if (activeCities.length > 0) {
          setSelectedCity(activeCities[0]._id);
        }
      })
      .catch((err) => console.error("Erreur chargement villes :", err));
  }, []);

  // Charger les statistiques
  const fetchStats = async () => {
    if (!selectedCity) return;
    try {
      setStatsLoading(true);
      const data = await synchronizationService.getVehicleStats(selectedCity);
      if (data) {
        setStats(data);
      }
    } catch (e) {
      console.error('Erreur chargement stats véhicules :', e);
    } finally {
      setStatsLoading(false);
    }
  };

  // Charger la liste des véhicules non présents dans Tigoon
  const fetchList = async () => {
    if (!selectedCity) return;
    try {
      setLoading(true);
      const params = { page, limit, cityId: selectedCity };
      if (q && q.trim()) {
        params.q = q.trim();
      }

      const data = await synchronizationService.getVehiclesNotInTigoon(params);

      if (!data) {
        setItems([]);
        setTotal(0);
        return;
      }

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement véhicules :', e);
      message.error('Erreur lors du chargement de la liste');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, q, selectedCity]);

  // Synchroniser un véhicule
  const handleSyncVehicle = async (carId) => {
    try {
      setSyncingCarId(carId);
      await synchronizationService.syncVehicle({ carId });
      message.success('Véhicule synchronisé avec succès !');
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur sync véhicule :', error);
      const errorMsg = error?.message || 'Une erreur est survenue';
      message.error(errorMsg);
    } finally {
      setSyncingCarId(null);
    }
  };

  // Synchroniser tous les véhicules
  const handleSyncAll = async () => {
    try {
      setBulkSyncing(true);
      const result = await synchronizationService.syncAllVehicles();
      message.success(`Synchronisation terminée : ${result.synced} véhicules ajoutés`);
      if (result.errors > 0) {
        message.warning(`${result.errors} erreurs rencontrées`);
      }
      fetchList();
      fetchStats();
    } catch (error) {
      console.error('Erreur sync en masse :', error);
      message.error('Erreur lors de la synchronisation en masse');
    } finally {
      setBulkSyncing(false);
    }
  };

  // Configuration des status
  const statusConfig = {
    working: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'En service', icon: <CheckCircleOutlined /> },
    not_working: { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Hors service', icon: <CloseCircleOutlined /> },
    repairing: { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'En réparation', icon: <ToolOutlined /> },
    pending: { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'En attente', icon: <ClockCircleOutlined /> }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar
            size={isMobile ? 36 : 44}
            style={{
              backgroundColor: getAvatarColor(record.immatriculation),
              fontWeight: 600
            }}
            icon={<CarOutlined />}
          />
          <div>
            <div style={{ fontWeight: 600, color: '#262626', fontFamily: 'monospace', fontSize: 14 }}>
              {record.immatriculation || 'N/A'}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {record.marque} {record.modele}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Marque / Modèle',
      key: 'marqueModele',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, color: '#262626' }}>{record.marque || 'N/A'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{record.modele || '-'}</div>
        </div>
      ),
      responsive: ['lg'],
    },
    {
      title: 'Année',
      dataIndex: 'annee',
      key: 'annee',
      render: (annee) => (
        <Tag style={{ borderRadius: 6 }}>{annee || 'N/A'}</Tag>
      ),
      responsive: ['md'],
    },
    {
      title: 'Status Yandex',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = statusConfig[status] || { color: '#8c8c8c', bg: '#fafafa', border: '#d9d9d9', label: status || 'N/A', icon: null };
        return (
          <Tag
            icon={config.icon}
            style={{
              backgroundColor: config.bg,
              color: config.color,
              border: `1px solid ${config.border}`,
              borderRadius: 6,
              padding: '2px 8px'
            }}
          >
            {config.label}
          </Tag>
        );
      },
      responsive: ['md'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Can perm="sync:update">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size={controlSize}
            loading={syncingCarId === record.carId}
            onClick={() => handleSyncVehicle(record.carId)}
            style={{
              borderRadius: 8,
              background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
              border: 'none'
            }}
          >
            {isMobile ? '' : 'Ajouter à Tigoon'}
          </Button>
        </Can>
      ),
    },
  ];

  // Gestion du changement de tableau
  const onTableChange = (pagination) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
  };

  return (
    <div className="content-wrapper">
      <style>
        {`
          .sync-vehicules-table .ant-table-thead > tr > th {
            background: #fafafa !important;
            font-weight: 600;
            color: #595959;
          }
          .sync-vehicules-table .ant-table-tbody > tr:nth-child(odd) {
            background: #ffffff;
          }
          .sync-vehicules-table .ant-table-tbody > tr:nth-child(even) {
            background: #fafafa;
          }
          .sync-vehicules-table .ant-table-tbody > tr:hover > td {
            background: #e6fffb !important;
          }
          .stat-card-vehicules {
            border-radius: 12px;
            border: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            transition: transform 0.2s, box-shadow 0.2s;
          }
          .stat-card-vehicules:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#13c2c2' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#13c2c2' }}>Configuration</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Véhicules manquants</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Cartes de statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-vehicules"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Véhicules Yandex</span>}
                value={stats.totalYandex}
                prefix={<CarOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-vehicules"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #b7eb8f 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Dans Tigoon</span>}
                value={stats.totalTigoon}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-vehicules"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)' }}
            >
              <Statistic
                title={<span style={{ color: '#595959', fontWeight: 500 }}>Manquants</span>}
                value={stats.notInTigoon}
                prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
                valueStyle={{ color: '#fa8c16', fontWeight: 600 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card
              className="stat-card-vehicules"
              loading={statsLoading}
              bodyStyle={{ padding: 16 }}
              style={{ background: 'linear-gradient(135deg, #e6fffb 0%, #87e8de 100%)' }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: '#595959', fontWeight: 500, fontSize: 14 }}>Taux de sync</span>
              </div>
              <Progress
                percent={stats.syncRate || 0}
                strokeColor={{
                  '0%': '#13c2c2',
                  '100%': '#87e8de',
                }}
                format={(percent) => <span style={{ fontWeight: 600, color: '#13c2c2' }}>{Number(percent || 0).toFixed(1)}%</span>}
              />
            </Card>
          </Col>
        </Row>

        {/* Info Alert */}
        <Alert
          message="Synchronisation des véhicules Yandex"
          description={
            <span>
              Cette liste affiche les véhicules présents dans <strong>Yandex</strong> mais absents de votre base <strong>Tigoon</strong>.
              Cliquez sur <strong>'Ajouter à Tigoon'</strong> pour synchroniser un véhicule individuellement,
              ou <strong>'Tout synchroniser'</strong> pour les ajouter tous en une fois.
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 24, borderRadius: 12 }}
        />

        {/* Carte avec filtre et tableau */}
        <Card
          style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
        >
          {/* Barre de recherche et actions */}
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8} md={5}>
              <Select
                placeholder="Sélectionner une ville"
                value={selectedCity}
                onChange={(val) => { setSelectedCity(val); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                size={controlSize}
                suffixIcon={<GlobalOutlined style={{ color: '#13c2c2' }} />}
              >
                {cities.map(city => (
                  <Select.Option key={city._id} value={city._id}>
                    {city.name} {city.isDefault && '(Par défaut)'}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={10} md={8}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Rechercher par immatriculation, marque..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                style={{ width: '100%', borderRadius: 8 }}
                allowClear
                size={controlSize}
              />
            </Col>
            <Col xs={24} sm={6} md={11} style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                <CarOutlined style={{ marginRight: 6 }} />
                {total} manquant{total > 1 ? 's' : ''}
              </div>
              <Button
                icon={<ReloadOutlined />}
                size={controlSize}
                onClick={() => { fetchList(); fetchStats(); }}
                style={{ borderRadius: 8 }}
              >
                Actualiser
              </Button>
              <Can perm="sync:update">
                <Popconfirm
                  title="Synchroniser tous les véhicules ?"
                  description={`${stats.notInTigoon} véhicule(s) seront ajoutés à Tigoon.`}
                  onConfirm={handleSyncAll}
                  okText="Oui, synchroniser"
                  cancelText="Annuler"
                  disabled={stats.notInTigoon === 0}
                >
                  <Button
                    type="primary"
                    icon={<CloudDownloadOutlined />}
                    size={controlSize}
                    loading={bulkSyncing}
                    disabled={stats.notInTigoon === 0}
                    style={{
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
                      border: 'none'
                    }}
                  >
                    Tout sync
                  </Button>
                </Popconfirm>
              </Can>
            </Col>
          </Row>

          {/* Tableau */}
          <Table
            className="sync-vehicules-table"
            rowKey="carId"
            columns={columns}
            dataSource={items}
            loading={isLoading}
            onChange={onTableChange}
            size={isMobile ? 'small' : 'middle'}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total, range) => (
                <span style={{ color: '#8c8c8c' }}>
                  {range[0]}-{range[1]} sur {total}
                </span>
              ),
            }}
            locale={{
              emptyText: (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
                  <div style={{ color: '#52c41a', fontSize: 16, fontWeight: 500 }}>
                    Tous les véhicules sont synchronisés !
                  </div>
                  <div style={{ color: '#8c8c8c', marginTop: 8 }}>
                    Aucun véhicule Yandex manquant dans Tigoon
                  </div>
                </div>
              )
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default SyncVehicules;
